import { useCallback } from "react";

import { usePlayerStore } from "../../store/usePlayerStore";

type SubscribeCallback = () => void;
type SubscribeCallbackWithProps<T> = (values: T) => void;

interface StoreSubscribe {
  onError: (cb: SubscribeCallback) => () => void;
  onFinish: (cb: SubscribeCallback) => () => void;
  onMediaChanged: (cb: SubscribeCallback) => () => void;
  onPause: (cb: SubscribeCallback) => () => void;
  onPipModeChange: (cb: SubscribeCallbackWithProps<boolean>) => () => void;
  onPlay: (cb: SubscribeCallback) => () => void;
  onPlayerDestroyed: (cb: SubscribeCallback) => () => void;
  onProgress: (cb: SubscribeCallback) => () => void;
  onRateChange: (cb: SubscribeCallback) => () => void;
  onSeekEnd: (cb: SubscribeCallback) => () => void;
  onSeekStart: (cb: SubscribeCallback) => () => void;
  onTimeUpdate: (cb: SubscribeCallbackWithProps<number>) => () => void;
  onVolumeChange: (cb: SubscribeCallback) => () => void;
  onWaiting: (cb: SubscribeCallback) => () => void;
}

export const useStoreSubscribe = (): StoreSubscribe => {
  const { subscribe } = usePlayerStore();

  const onError = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => !!state.error,
        error => error && cb()
      ),
    [subscribe]
  );

  const onFinish = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.isEnded,
        isEnded => {
          if (isEnded) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onMediaChanged = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.programId,
        (programId, prevProgramId) => {
          if (prevProgramId && programId && prevProgramId !== programId) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onPause = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.isPaused,
        isPaused => {
          if (isPaused) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onPipModeChange = useCallback(
    (cb: SubscribeCallbackWithProps<boolean>) =>
      subscribe(
        state => state.isPictureInPicture,
        isPictureInPicture => cb(isPictureInPicture)
      ),
    [subscribe]
  );

  const onPlay = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.isPaused,
        isPaused => {
          if (!isPaused) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onPlayerDestroyed = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.isDestroyed,
        isDestroyed => {
          if (isDestroyed) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onProgress = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.buffered,
        (buffered, prevBuffered) => {
          if (buffered !== prevBuffered) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onRateChange = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.playbackRate,
        (rate, prevRate) => {
          if (rate !== prevRate) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onSeekEnd = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.isSeeking,
        isSeeking => {
          if (!isSeeking) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onSeekStart = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.isSeeking,
        isSeeking => {
          if (isSeeking) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onTimeUpdate = useCallback(
    (cb: SubscribeCallbackWithProps<number>) =>
      subscribe(
        state => state.currentTime,
        currentTime => cb(currentTime)
      ),
    [subscribe]
  );

  const onVolumeChange = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => [state.volume, state.isMuted],
        ([volume, muted], [prevVolume, prevMuted]) => {
          if (volume !== prevVolume || muted !== prevMuted) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  const onWaiting = useCallback(
    (cb: SubscribeCallback) =>
      subscribe(
        state => state.isLoading,
        isLoading => {
          if (isLoading) {
            cb();
          }
        }
      ),
    [subscribe]
  );

  return {
    onError,
    onFinish,
    onMediaChanged,
    onPause,
    onPipModeChange,
    onPlay,
    onPlayerDestroyed,
    onProgress,
    onRateChange,
    onSeekEnd,
    onSeekStart,
    onTimeUpdate,
    onVolumeChange,
    onWaiting
  };
};
